import React from 'react';
import { useCapabilities, useAllowedTabs } from '../../../utils/capabilities';
import { useSettingsRequest } from './useSettingsRequest';
import ScenariosSection from './Scenarios/ScenariosSection';
import OrganizationSection from './Organization/OrganizationSection';
import UsersSection from './Users/UsersSection';
import ProfileSection from './Profile/ProfileSection';
import PermissionsSection from './Permissions/PermissionsSection';
import '../Library/Library.css'; // library-section-head, library-tab, library-panel
import './NavySettings.css';

/**
 * Settings for Navy Phase 0 (handoff story 30), shown while `navy-assessments`
 * is on: Organization, Users, Profile, Permissions and Assessments, in the
 * prototype's order. Anything else is absent rather than locked. The first four
 * run on the SettingsModel's sample data; Assessments is PRD-1701's scenarios.
 *
 * With `role-capabilities` on, only the tabs the role allows show (PRD-1699).
 */
function NavySettings({
  model,
  libraryModel,
  assessmentsModel = null,
  me,
  onShowToast,
  requestedTab = null,
  addScenarioOnOpen = false,
  onReturnToAddAssessment,
  onRequestHandled,
}) {
  const caps = useCapabilities(me?.features);
  const { shown: tabs, active, setActive } = useAllowedTabs(caps, [
    { id: 'organization', label: 'Organization', allowed: caps.has('editOrganization') || caps.has('manageSso') },
    { id: 'users', label: 'Users', allowed: caps.has('manageUsers') },
    { id: 'profile', label: 'Profile', allowed: caps.has('updateProfile') },
    { id: 'permissions', label: 'Permissions', allowed: caps.has('managePermissions') },
    { id: 'assessments', label: 'Assessments', allowed: caps.has('assessmentSetup') },
  ]);
  const { returnToAdd, backToAddAssessment } = useSettingsRequest({
    tabs, setActiveTab: setActive, requestedTab, addScenarioOnOpen, onRequestHandled, assessmentsModel, onReturnToAddAssessment,
  });

  return (
    <div className="navy-settings">
      <div className="library-section-head">
        <div className="library-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`library-tab${active === tab.id ? ' is-active' : ''}`}
              aria-current={active === tab.id ? 'page' : undefined}
              data-label={tab.label}
              onClick={() => setActive(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {active === 'organization' && <OrganizationSection model={model} onShowToast={onShowToast} />}
      {active === 'users' && <UsersSection model={model} onShowToast={onShowToast} />}
      {active === 'profile' && <ProfileSection model={model} onShowToast={onShowToast} />}
      {active === 'permissions' && <PermissionsSection model={model} onShowToast={onShowToast} />}
      {active === 'assessments' && (
        <ScenariosSection
          model={libraryModel}
          onShowNotification={onShowToast}
          addOnOpen={addScenarioOnOpen}
          onAddOpened={onRequestHandled}
          returnToAdd={returnToAdd}
          onReturnToAdd={backToAddAssessment}
        />
      )}
    </div>
  );
}

export default NavySettings;
